import { Vue, Component, Prop } from 'vue-property-decorator'
import ConfigurationRelationships
    from '@/modules/configurationModule/shared/components/configuration-relationships/configuration-relationships.vue'
import { CommonParams, CommonFilterModel } from '@/api/resources/common/shared/models/common.models'
import { ConfigurationAssayResponse } from '@/modules/configurationModule/detail/assays/configuration-assays/services/configuration-assay/configuration-assay.dto'
import { ConfigurationAutomaticAssayOntologyCreateRequest, ConfigurationAutomaticOntologyAssayResponse } from '@/modules/configurationModule/detail/automation/assays/services/configuration-automatic-ontology-assay/configuration-automatic-ontology-assay.dto'
import { ConfigurationOntologyForAutomaticAssayResponse, ConfigurationOntologyForAutomaticAssayCreateRequest } from '@/modules/configurationModule/detail/automation/assays/services/configuration-ontology-for-automatic-assay/configuration-ontology-for-automatic-assay.dto'
import { configurationOntologyForAutomaticAssayService } from '@/modules/configurationModule/detail/automation/assays/services/configuration-ontology-for-automatic-assay/configuration-ontology-for-automatic-assay.service'
import { EntityIdentifiersRequest, EntityIdentifierWithErrorResponse } from '@/shared/models/http/http.model'
import { PAGINATOR_BIG_PAGE_SIZE } from '@/shared/services/services-constants'
import { configurationAutomaticOntologyAssayService } from '@/modules/configurationModule/detail/automation/assays/services/configuration-automatic-ontology-assay/configuration-automatic-ontology-assay.service'
import { ConfigurationAutomationAssaysModalAssaysFormState, initialConfigurationAutomationAssaysModalAssaysFormState } from '@/modules/configurationModule/detail/automation/assays/components/assays-modal-relationships/configuration-automation-assays-modal-relationships.model'
import { Nullable } from '@/shared/types/app.types'
import { IResponseAgGrid } from '@/shared/models/ag-grid.model'
import { configurationAssayLaboratoryService } from '@/modules/configurationModule/detail/local-assays/assays-laboratory/services/configuration-assay-laboratory/configuration-assay-laboratory.service'
import { ConfigurationAssayLaboratoryResponse } from '@/modules/configurationModule/detail/local-assays/assays-laboratory/services/configuration-assay-laboratory/configuration-assay-laboratory.dto'
import { GridUtils } from '@/shared/util/grid-utils'

@Component({
    components: {
        ConfigurationRelationships
    }
})
export default class ConfigurationAutomationAssaysModalRelationships extends Vue {
    @Prop(String) readonly actionTitle!: string
    @Prop() readonly ontologyForAutomaticAssay!: ConfigurationOntologyForAutomaticAssayResponse

    state: ConfigurationAutomationAssaysModalAssaysFormState
    constructor() {
        super()
        this.state = initialConfigurationAutomationAssaysModalAssaysFormState()
        this.state.ontologyForAutomaticAssay = this.ontologyForAutomaticAssay
    }

    get updateMode(): boolean {
        return !!this.state.ontologyForAutomaticAssay?.id
    }

    private async _loadAssays(): Promise<void> {
        this.state.assays = await this._getAssays()
    }

    private async _loadAutomaticOntologyAssays(): Promise<void> {
        this.state.automaticOntologyAssays = await this._getAutomaticOntologyAssays()
    }

    async loadData() {
        await this._loadAssays()
        await this._loadAutomaticOntologyAssays()
    }

    /// SERVICES CALLS ///

    private async _getAssays(): Promise<ConfigurationAssayResponse[]> {
        const params: CommonParams = {
            size: PAGINATOR_BIG_PAGE_SIZE
        }
        const filterModel = {
            'assay.active': {
                filterType: 'boolean',
                type: 'equals',
                filter: true
            }
        }
        const response: Nullable<IResponseAgGrid<ConfigurationAssayLaboratoryResponse>> = await this.$_toCallSecure(configurationAssayLaboratoryService.paginator(filterModel, params, this.state.laboratoryId))
        return response?.content.map((item: ConfigurationAssayLaboratoryResponse) => item.assay) ?? []
    }

    private async _getAutomaticOntologyAssays(): Promise<ConfigurationAutomaticOntologyAssayResponse[]> {
        const filterModel: CommonFilterModel = {
            'ontologyForAutomaticAssay.id': GridUtils.getNumberFilterModel('equals', this.state.ontologyForAutomaticAssay!.id)
        }
        const params: CommonParams = {
            size: PAGINATOR_BIG_PAGE_SIZE
        }
        const response = await this.$_toCallSecure(configurationAutomaticOntologyAssayService.paginator(filterModel, params, this.state.laboratoryId))
        if (response) {
            return response.content
        }
        return []
    }

    private async _createAutomaticOntologyAssay(createRequest: ConfigurationAutomaticAssayOntologyCreateRequest): Promise<ConfigurationAutomaticOntologyAssayResponse | null> {
        const response = await this.$_toCallSecure(configurationAutomaticOntologyAssayService.create(this.state.laboratoryId, createRequest))
        if (response) {
            this.$_showToastSuccess(this.$tc('configuration.toastMessages.created'))
            return response
        }
        return null
    }

    private async _deleteAutomaticOntologyAssay(requestDelete: EntityIdentifiersRequest): Promise<EntityIdentifierWithErrorResponse[] | null> {
        const ids = requestDelete?.entityIdentifiers
        if (ids?.length) {
            const response = await this.$_toCallSecure(configurationAutomaticOntologyAssayService.delete(this.state.laboratoryId, requestDelete))
            if (response) {
                const elements = ids.length - response.length
                if (elements > 0) {
                    this.$_showToastSuccess(this.$tc('configuration.toastMessages.deleted'))
                    return response
                }
            }
        }
        return null
    }

    private _createOntologyForAutomaticAssay(createRequest: ConfigurationOntologyForAutomaticAssayCreateRequest): Promise<ConfigurationOntologyForAutomaticAssayResponse | null> {
        return this.$_toCallSecure(configurationOntologyForAutomaticAssayService.create(this.state.laboratoryId, createRequest))
    }

    /// FORM OPERATIONS ///

    async createOperation(assay: ConfigurationAssayResponse): Promise<void> {
        if (assay) {
            if (!this.updateMode) {
                this.state.ontologyForAutomaticAssay = await this._createOntologyForAutomaticAssay({
                    sampleTypeId: this.ontologyForAutomaticAssay.sampleType.id,
                    topographicCodeId: this.ontologyForAutomaticAssay.topographicCode?.id,
                    procedureId: this.ontologyForAutomaticAssay.procedure?.id,
                    mostLikelyMorphologicCodeId: this.ontologyForAutomaticAssay.mostLikelyMorphologicCode?.id
                })
            }

            if (this.state.ontologyForAutomaticAssay) {
                const createRequest: ConfigurationAutomaticAssayOntologyCreateRequest = {
                    ontologyForAutomaticAssayId: this.state.ontologyForAutomaticAssay.id,
                    assayId: assay.id
                }
                if (await this._createAutomaticOntologyAssay(createRequest)) {
                    await this._loadAutomaticOntologyAssays()
                }
            }
        }
    }

    async deleteOperation(automaticOntologyAssay: ConfigurationAutomaticOntologyAssayResponse): Promise<void> {
        if (automaticOntologyAssay) {
            const requestDelete: EntityIdentifiersRequest = {
                entityIdentifiers: [automaticOntologyAssay.id]
            }
            if (await this._deleteAutomaticOntologyAssay(requestDelete)) {
                await this._loadAutomaticOntologyAssays()
            }
        }
    }
}
